import { ApiError } from '../identity/types';
import { buildCsv } from './exportCsv';
import { CSV_SCHEMA_VERSION } from './exportCsv';
import { ExportErrorCode, ExportValidationError } from './exportErrors';
import {
  CATEGORIES, categoryLabel, categorySide,
  type AccountingExportCapabilities, type AccountingMapping, type AccountRef, type AddGroupMembersInput, type BreakdownLine, type ChartAccount, type ChartAccountType,
  type ChartImportResult, type CodeBulkAction, type CodeBulkApplyInput, type CodeBulkPreview, type CodeBulkPreviewItem, type CodeBulkRequest, type CodeBulkResult, type CodeReadiness,
  type CodeReadinessItem, type CodeReadinessParams, type CoverageStatus, type CreateChartAccountInput, type CreateEmployeeGroupInput, type CreateExportInput,
  type CreateExportResult, type CreateMappingInput, type DefaultAccountRef, type DownloadedExport, type EligiblePayrollParams, type EligiblePayrolls, type EmployeeCandidate,
  type EmployeeGroup, type EmployeeGroupMember, type ExcludedItem, type ExportAuditItem, type ExportDetail, type ExportKind, type ExportMessage, type ExportMode, type ExportOptions,
  type ExportOptionsInput, type ExportPreview, type ExportSettings, type ExportStatus, type ExportSummary, type JournalLine, type JournalSide, type ListChartAccountsParams,
  type ListExportsParams, type ListMappingsParams, type MappingReadiness, type Paged, type PreviewEmployee, type PreviewParams, type ReplacementInput, type SaveExportSettingsInput,
  type SourceCoverageItem, type SourceLine, type UnmappedItem, type UnmappedReason, type UpdateChartAccountInput, type UpdateEmployeeGroupInput, type WageGroupDimension,
  type WageGroupGrid, type WageGroupLine, type WageGroupParams, type WageGroupRow, type WageGroupStatus,
  CHART_ACCOUNT_TYPES, NET_PAY_DETAIL_MODES, SELECTABLE_WAGE_MODES, WAGE_GROUP_DIMENSIONS,
} from './exportTypes';
import { CODE_MAPPING_BANNER } from './exportOptions';
import { subcategoryLabel } from './exportFormat';
import { formatCents, plainDecimal, sumCents, type Cents } from './money';
import {
  MOCK_COMPANY_ID, MOCK_EMPLOYEES, MOCK_SCHEDULES, RUN_CORRECTED_ID, RUN_EXPORTED_ID, RUN_READY_ID, SEED_ACCOUNTS, SEED_MAPPINGS, TAX_SUBCATEGORY_BY_CODE,
  buildMockRuns, type MockCalculation, type MockRun,
} from './mockExportData';
import { DIMENSION_NAMES, EMPLOYMENT_TYPE_LABEL, MOCK_CODES, MOCK_EMPLOYEE_ATTRIBUTES, MOCK_GROUP_SEEDS, payrollCodeKey } from './mockWageGroupData';
import { accountingCapsScenario, accountingScenario, scenarioExportCapabilities } from './mockAccountingScenario';
import { sha256Hex } from './sha256';

/**
 * The in-memory "server" behind MockAccountingGateway for the Payroll Accounting Export module. It follows the contract
 * (docs/payroll-accounting-export-contract.md): the same source-line builder, mapping precedence, exact integer balance and completeness checks,
 * identity-based idempotency, lifecycle transitions, reversal and replacement links, append-only mapping versions, and chart-of-accounts rules,
 * all on integer cents and deterministic synthetic data. It is a test double, never a source of truth for the real engine.
 */

const apiError = (status: number, detail: string, code?: string) => new ApiError(status, detail, status >= 500 ? 'mock-operation-0001' : null, true, code ?? null);

const todayIso = () => new Date().toISOString().slice(0, 10);
const nowIso = () => new Date().toISOString();

// ---- internal shapes ----

interface InternalSource extends SourceLine {
  description: string;
  employeeId: string;
  departmentId: string;
  stateCode: string;
}

interface StoredMapping extends AccountingMapping {
  hiddenInScenario?: 'missing-mapping';
}

interface StoredMembership {
  membershipId: string;
  groupId: string;
  employeeId: string;
  effectiveFrom: string;
  effectiveTo: string | null;
}

/** The blockers the mapping options add; each blocks the export and explains itself (contract section 4). */
type OptionBlockerCode = 'NetPayNotConsolidatable' | 'DefaultWageAccountRequired' | 'WageGroupDimensionRequired' | 'GroupMappingMissing' | 'GroupMembershipMissing' | 'EmployeeGroupAmbiguous';

type DimensionValue = { kind: 'value'; key: string; label: string } | { kind: 'none' } | { kind: 'ambiguous' };

const BLOCKED_REASON: Record<OptionBlockerCode, string> = {
  NetPayNotConsolidatable: '',
  DefaultWageAccountRequired: 'No active company-default wage account.',
  WageGroupDimensionRequired: 'No group dimension was chosen.',
  GroupMappingMissing: 'No wage account is mapped to this group.',
  GroupMembershipMissing: 'These employees have no value for this dimension.',
  EmployeeGroupAmbiguous: 'These employees are in more than one active employee group.',
};

const CODE_KINDS_ORDER = ['Deduction', 'Earning', 'Benefit'];

/** What each legacy resolver level is called in a line's resolution reason (contract section 3). */
const LEGACY_LEVELS = ['EmployeeOverride', 'PayrollCode', 'Department', 'Location', 'CompanyDefault'] as const;
const LEGACY_LEVEL_TEXT = ['Employee override', 'Payroll-code mapping', 'Department mapping', 'State mapping', 'Company default mapping'] as const;

interface StoredExport {
  detail: ExportDetail;
  key: string;
  payrollVersion: number;
  reversalReference: string | null;
  audit: ExportAuditItem[];
}

interface MappedResolution {
  kind: 'mapped';
  mapping: StoredMapping;
  level: string;
  reason: string;
  group: { dimension: WageGroupDimension; key: string; label: string } | null;
}
type Resolution = MappedResolution | { kind: 'unmapped'; reason: UnmappedReason } | { kind: 'blocked'; code: OptionBlockerCode; label: string };

const CATEGORY_ORDER = new Map(CATEGORIES.map((cat, i) => [cat.key, i]));

const EXPENSE_CATEGORIES = new Set(['EarningsGross', 'EmployerTaxExpense', 'EmployerBenefitExpense', 'EmployerRetirementExpense', 'OtherEmployerExpense']);
const LIABILITY_CATEGORIES = new Set(['EmployeeTaxWithholding', 'EmployerTaxLiability', 'PreTaxDeductionPayable', 'PostTaxDeductionPayable', 'GarnishmentPayable', 'ReimbursementPayable']);

const COVERAGE: Record<string, { status: CoverageStatus; note: string }> = {
  EarningsGross: { status: 'PartiallySupplied', note: 'Gross earnings are supplied as one amount per calculation. Earning types (regular, overtime, bonus, commission, tips) are not supplied by the payroll calculation.' },
  EmployerTaxExpense: { status: 'Supplied', note: 'Supplied by tax type and jurisdiction.' },
  EmployerBenefitExpense: { status: 'NotSupplied', note: 'Not supplied by the payroll calculation.' },
  EmployerRetirementExpense: { status: 'NotSupplied', note: 'Not supplied by the payroll calculation.' },
  OtherEmployerExpense: { status: 'NotSupplied', note: 'Not supplied by the payroll calculation.' },
  EmployeeTaxWithholding: { status: 'Supplied', note: 'Supplied by tax type and jurisdiction.' },
  EmployerTaxLiability: { status: 'Supplied', note: 'Supplied by tax type and jurisdiction.' },
  PreTaxDeductionPayable: { status: 'PartiallySupplied', note: 'Supplied as one total. Deductions by code are not supplied by the payroll calculation.' },
  PostTaxDeductionPayable: { status: 'PartiallySupplied', note: 'Supplied as one total. Deductions by code are not supplied by the payroll calculation.' },
  GarnishmentPayable: { status: 'PartiallySupplied', note: 'Supplied as one total. Garnishment lines are not supplied by the payroll calculation.' },
  LoanRepaymentReceivable: { status: 'NotSupplied', note: 'Not supplied by the payroll calculation.' },
  ReimbursementPayable: { status: 'NotSupplied', note: 'Not supplied by the payroll calculation.' },
  NetPayClearing: { status: 'Supplied', note: 'Supplied as net pay per calculation.' },
};

export class MockAccountingExportStore {
  private chart: ChartAccount[] = [];
  private mappings: StoredMapping[] = [];
  private exports: StoredExport[] = [];
  private runs: MockRun[] = buildMockRuns();
  private mappingVersionCounter = 0;
  private idCounter = 0;
  /** Company export settings: unconfigured (the legacy defaults) until someone saves them. */
  private settings: ExportSettings = {
    netPayDetailMode: 'Consolidated', wageMappingMode: 'LegacyResolver', wageGroupDimension: null, settingsVersion: 0, groupConfigVersion: 0, isConfigured: false, updatedAtUtc: null,
    updatedByUserId: null, defaultWageAccount: null, defaultClearingAccount: null,
  };
  private groups: EmployeeGroup[] = [];
  private memberships: StoredMembership[] = [];
  /** Incremented whenever a group or membership changes, so an export's identity depends on the group data it was built from. */
  private groupConfigVersion = 0;
  private bulkLedger = new Map<string, { fingerprint: string; result: CodeBulkResult }>();
  /** While seeding, timestamps are fixed so the synthetic history is deterministic. */
  private fixedClock: string | null = null;

  constructor() {
    this.seed();
  }

  private id(prefix: string): string {
    this.idCounter += 1;
    return `${prefix}-${String(this.idCounter).padStart(4, '0')}`;
  }

  /** The synthetic history is always built as if no scenario switch were set. */
  private scenario() {
    return this.fixedClock ? null : accountingScenario();
  }

  private now(): string {
    return this.fixedClock ?? nowIso();
  }

  // ---------------------------------------------------------------- capabilities and permission

  getCapabilities(): AccountingExportCapabilities {
    return scenarioExportCapabilities(accountingCapsScenario());
  }

  private require(flag: keyof AccountingExportCapabilities, what = 'do that'): void {
    if (!this.getCapabilities()[flag]) throw apiError(403, `You do not have permission to ${what}.`);
  }

  // ---------------------------------------------------------------- seed

  private seed(): void {
    this.fixedClock = '2026-10-02T09:00:00.000Z';
    for (const a of SEED_ACCOUNTS) {
      this.chart.push({
        id: this.id('acct'), accountNumber: a.number, name: a.name, accountType: a.type, isActive: a.active !== false, source: 'Manual', externalReference: null, mappingUseCount: 0,
      });
    }
    for (const m of SEED_MAPPINGS) {
      const account = this.chart.find((a) => a.accountNumber === m.account)!;
      this.mappingVersionCounter += 1;
      const category = m.category;
      this.mappings.push({
        id: this.id('map'), category, subcategory: m.subcategory ?? null, payrollCode: null, employeeId: null, departmentId: null, locationCode: m.locationCode ?? null, groupDimension: null, groupKey: null,
        treatment: 'Post', nonPostingReason: null, chartAccountId: account.id, accountNumber: account.accountNumber, accountName: account.name, accountType: account.accountType,
        direction: categorySide(category) ?? 'Debit', effectiveFrom: '2026-01-01', effectiveTo: null, mappingVersion: this.mappingVersionCounter, isActive: true,
        confirmedFundingModel: false, ...(m.hiddenInScenario ? { hiddenInScenario: m.hiddenInScenario } : {}),
      });
    }

    // Two synthetic company-defined employee groups (nobody is assigned by default in any real environment).
    for (const g of MOCK_GROUP_SEEDS) {
      this.groups.push({ id: g.id, name: g.name, description: g.description, isActive: true, effectiveFrom: '2026-01-01', effectiveTo: null, memberCount: 0, concurrencyVersion: 1 });
      for (const employeeId of g.members) this.memberships.push({ membershipId: `${g.id}-${employeeId}`, groupId: g.id, employeeId, effectiveFrom: '2026-01-01', effectiveTo: null });
    }
    this.groupConfigVersion = 1;

    // History: an exported and downloaded run, and a run that was corrected after it was exported (reversal required). These were "made before options existed": legacy.
    const exported = this.requireRun(RUN_EXPORTED_ID);
    this.fixedClock = '2026-10-02T10:15:00.000Z';
    const first = this.build(exported, exported.currentVersion, 'Compound', exported.checkDate, 'Original', null, null, null, true, false, undefined, undefined, this.legacyRecord('Compound'));
    this.fixedClock = '2026-10-02T10:20:00.000Z';
    this.markDownloaded(first);

    const corrected = this.requireRun(RUN_CORRECTED_ID);
    this.fixedClock = '2026-10-01T08:00:00.000Z';
    const second = this.build(corrected, 1, 'Compound', corrected.checkDate, 'Original', null, null, null, true, false, undefined, undefined, this.legacyRecord('Compound'));
    this.fixedClock = '2026-10-01T08:05:00.000Z';
    this.markDownloaded(second);
    corrected.currentVersion = 2;
    this.fixedClock = '2026-10-03T11:00:00.000Z';
    this.syncOne(second);
    this.fixedClock = null;
    this.recomputeUseCounts();
  }

  private requireRun(id: string): MockRun {
    const run = this.runs.find((r) => r.id === id);
    if (!run) throw apiError(404, 'That payroll run was not found for this company.', 'PAYROLL_NOT_FOUND');
    return run;
  }

  private recomputeUseCounts(): void {
    for (const a of this.chart) a.mappingUseCount = this.mappings.filter((m) => m.isActive && m.chartAccountId === a.id).length;
  }

  // ---------------------------------------------------------------- eligible payrolls

  listEligiblePayrolls(params?: EligiblePayrollParams): EligiblePayrolls {
    this.require('canPreview', 'view accounting exports');
    const scenario = this.scenario();
    const items = scenario === 'no-payrolls' ? [] : this.runs
      .filter((r) => (!params?.payScheduleId || r.payScheduleId === params.payScheduleId) && (!params?.from || r.checkDate >= params.from) && (!params?.to || r.checkDate <= params.to))
      .sort((a, b) => (a.checkDate < b.checkDate ? 1 : -1))
      .map((r) => {
        const calcs = this.calculationsFor(r, r.currentVersion);
        return {
          payrollRunId: r.id, payScheduleId: r.payScheduleId, payScheduleLabel: r.payScheduleLabel, periodStart: r.periodStart, periodEnd: r.periodEnd, checkDate: r.checkDate,
          employeeCount: calcs.length, grossEarningsCents: sumCents(calcs.map((x) => x.grossCents)), netPayCents: sumCents(calcs.map((x) => x.netPayCents)),
          exportStatus: this.runExportStatus(r.id),
        };
      });
    return { items, payScheduleOptions: MOCK_SCHEDULES.map((s) => ({ id: s.id, label: s.label })) };
  }

  private runExportStatus(runId: string) {
    const latest = this.exports
      .filter((e) => e.detail.payrollRunId === runId && e.detail.kind !== 'Reversal' && e.detail.status !== 'Failed')
      .sort((a, b) => (a.detail.generatedAtUtc < b.detail.generatedAtUtc ? 1 : -1))[0];
    return (latest ? latest.detail.status : 'None') as 'None' | 'Generated' | 'Downloaded' | 'ReversalRequired' | 'Reversed' | 'Superseded';
  }

  // ---------------------------------------------------------------- source lines, mapping resolution, journal

  /** The newest payroll's first calculation can be made to violate its own pay identity ("unbalanced" scenario). */
  private calculationsFor(run: MockRun, version: number): MockCalculation[] {
    const calcs = run.versions[version] ?? [];
    if (this.scenario() === 'unbalanced' && run.id === RUN_READY_ID && version === run.currentVersion) {
      return calcs.map((x, i) => (i === 0 ? { ...x, netPayCents: x.netPayCents + 1 } : x));
    }
    return calcs;
  }

  private sourceLinesOf(calcs: MockCalculation[]): InternalSource[] {
    const lines: InternalSource[] = [];
    for (const calc of calcs) {
      const emp = MOCK_EMPLOYEES[calc.employeeId]!;
      let ordinal = 0;
      const add = (category: string, subcategory: string | null, code: string | null, jurisdiction: string | null, side: JournalSide, amountCents: Cents, description: string) => {
        ordinal += 1;
        lines.push({
          id: sha256Hex(`${calc.calculationId}|${category}|${subcategory ?? ''}|${code ?? ''}|${jurisdiction ?? ''}|${ordinal}`).slice(0, 32), calculationId: calc.calculationId,
          employeeReference: emp.reference, category, subcategory, payrollCode: code, jurisdiction, side, amountCents, provenance: 'Calculation', posted: false,
          description, employeeId: emp.id, departmentId: emp.departmentId, stateCode: calc.stateCode,
        });
      };
      add('EarningsGross', null, null, null, 'Debit', calc.grossCents, 'Gross wages');
      for (const tax of calc.taxLines) {
        const sub = (tax.taxTypeCode && TAX_SUBCATEGORY_BY_CODE[tax.taxTypeCode]) || 'Other';
        const code = tax.taxTypeCode;
        if (tax.isEmployerPaid) {
          add('EmployerTaxExpense', sub, code, tax.jurisdiction, 'Debit', tax.amountCents, `Employer tax expense - ${tax.description}`);
          add('EmployerTaxLiability', sub, code, tax.jurisdiction, 'Credit', tax.amountCents, `Employer tax liability - ${tax.description}`);
        } else {
          add('EmployeeTaxWithholding', sub, code, tax.jurisdiction, 'Credit', tax.amountCents, `Employee tax withholding - ${tax.description}`);
        }
      }
      if (calc.preTaxCents !== 0) add('PreTaxDeductionPayable', null, null, null, 'Credit', calc.preTaxCents, 'Pre-tax deductions payable');
      if (calc.postTaxCents !== 0) add('PostTaxDeductionPayable', null, null, null, 'Credit', calc.postTaxCents, 'Post-tax deductions payable');
      if (calc.garnishmentsCents !== 0) add('GarnishmentPayable', null, null, null, 'Credit', calc.garnishmentsCents, 'Garnishments payable');
      add('NetPayClearing', null, null, null, 'Credit', calc.netPayCents, 'Net pay to Payroll Clearing');
    }
    return lines;
  }

  private activeMappings(accountingDate: string): StoredMapping[] {
    const scenario = this.scenario();
    return this.mappings.filter((m) => m.isActive && !(m.hiddenInScenario && m.hiddenInScenario === scenario) && m.effectiveFrom <= accountingDate && (m.effectiveTo === null || accountingDate <= m.effectiveTo));
  }

  /** Precedence (contract section 4): employee override, payroll code, department or location (department first), company default; the mapping that
   * also matches the subcategory beats one without; a remaining tie is blocked as ambiguous, never silently picked. */
  private resolve(line: InternalSource, accountingDate: string): Resolution {
    const accountOf = (m: StoredMapping) => (m.chartAccountId ? this.chart.find((a) => a.id === m.chartAccountId) : undefined);
    // The original resolver knows nothing of group qualifiers: a group mapping never applies here (adding one cannot change a legacy export).
    const all = this.activeMappings(accountingDate).filter((m) => !m.groupDimension && m.category === line.category && (m.subcategory === null || m.subcategory === line.subcategory));
    const usable = all.filter((m) => m.treatment === 'NonPosting' || accountOf(m)?.isActive);
    const levels: ((m: StoredMapping) => boolean)[] = [
      (m) => m.employeeId === line.employeeId,
      (m) => m.employeeId === null && m.payrollCode !== null && m.payrollCode === line.payrollCode,
      (m) => m.employeeId === null && m.payrollCode === null && m.departmentId !== null && m.departmentId === line.departmentId,
      (m) => m.employeeId === null && m.payrollCode === null && m.departmentId === null && m.locationCode !== null && m.locationCode === line.stateCode,
      (m) => m.employeeId === null && m.payrollCode === null && m.departmentId === null && m.locationCode === null,
    ];
    const pick = (candidates: StoredMapping[]): { pool: StoredMapping[]; level: number } | null => {
      for (const [level, matches] of levels.entries()) {
        const atLevel = candidates.filter(matches);
        if (atLevel.length === 0) continue;
        const specific = atLevel.filter((m) => m.subcategory !== null);
        return { pool: specific.length > 0 ? specific : atLevel, level };
      }
      return null;
    };
    const found = pick(usable);
    if (found) {
      if (found.pool.length > 1) return { kind: 'unmapped', reason: 'Ambiguous' };
      const mapping = found.pool[0]!;
      return { kind: 'mapped', mapping, level: LEGACY_LEVELS[found.level]!, reason: `${LEGACY_LEVEL_TEXT[found.level]} (version ${mapping.mappingVersion})`, group: null };
    }
    // Nothing usable: it is an archived account when the mapping that WOULD apply points at one, otherwise there is simply no mapping.
    return { kind: 'unmapped', reason: pick(all) ? 'ArchivedAccount' : 'NoMapping' };
  }

  /** Builds the full validation report for a payroll version (the preview and the 422 body share it). Never persists anything. */
  private evaluate(run: MockRun, version: number, mode: ExportMode, accountingDate: string, kind: ExportKind, reversalOfId: string | null, replacesId: string | null, options: ExportOptions): ExportPreview & { _lines: JournalLine[] } {
    const caps = this.getCapabilities();
    const calcs = this.calculationsFor(run, version);
    const source = this.sourceLinesOf(calcs);
    const blockers: ExportMessage[] = [];
    const warnings: ExportMessage[] = [];
    const legacy = options.source === 'LegacyDefault';

    const key = this.keyFor(run, version, mode, accountingDate, kind, reversalOfId, replacesId, options);
    const reference = `PAYEXP-${accountingDate.replace(/-/g, '')}-${key.slice(0, 8).toUpperCase()}`;

    // Per-calculation identity: gross - pre-tax - employee taxes - post-tax - garnishments == net pay, exactly.
    for (const calc of calcs) {
      const employeeTaxes = sumCents(calc.taxLines.filter((t) => !t.isEmployerPaid).map((t) => t.amountCents));
      if (calc.grossCents - calc.preTaxCents - employeeTaxes - calc.postTaxCents - calc.garnishmentsCents !== calc.netPayCents) {
        blockers.push({ code: 'SourceInconsistent', message: `Calculation ${calc.calculationId} does not add up: gross less deductions and taxes does not equal net pay. Payroll must be corrected before it can be exported.` });
      }
    }

    type Posted = { line: InternalSource; mapping: StoredMapping; side: JournalSide; amountCents: Cents; flipped: boolean; res: MappedResolution };
    const posted: Posted[] = [];
    const excludedLines: { line: InternalSource; mapping: StoredMapping }[] = [];
    const unmappedLines: { line: InternalSource; reason: UnmappedReason }[] = [];
    const optionBlocked: { line: InternalSource; code: OptionBlockerCode; label: string }[] = [];
    for (const line of source) {
      const result = this.resolveLine(line, accountingDate, options);
      if (result.kind === 'unmapped') unmappedLines.push({ line, reason: result.reason });
      else if (result.kind === 'blocked') optionBlocked.push({ line, code: result.code, label: result.label });
      else if (result.mapping.treatment === 'NonPosting') excludedLines.push({ line, mapping: result.mapping });
      else {
        // A negative source amount posts on the opposite side as a positive amount.
        const flipped = line.amountCents < 0;
        posted.push({ line, mapping: result.mapping, side: flipped ? (line.side === 'Debit' ? 'Credit' : 'Debit') : line.side, amountCents: Math.abs(line.amountCents), flipped, res: result });
      }
    }

    // Consolidated net pay is valid only when ALL net pay resolves to ONE (account, side, dimension) combination: amounts that belong to different accounts are never merged.
    const consolidated = !legacy && mode === 'Compound' && options.netPayDetailMode === 'Consolidated';
    if (consolidated) {
      const netPay = posted.filter((p) => p.line.category === 'NetPayClearing');
      const combos = new Map<string, string>();
      for (const p of netPay) combos.set([p.side, p.mapping.accountNumber, p.mapping.departmentId, p.mapping.locationCode].join('|'), `${p.mapping.accountNumber} ${p.mapping.accountName}`);
      if (combos.size > 1) {
        blockers.push({
          code: 'NetPayNotConsolidatable',
          message: `Net pay resolves to ${combos.size} different accounts (${[...combos.values()].join('; ')}), so it cannot be combined into one Payroll Clearing line. Amounts for different accounts are never merged.`,
        });
        for (const p of netPay) optionBlocked.push({ line: p.line, code: 'NetPayNotConsolidatable', label: '' });
        for (let i = posted.length - 1; i >= 0; i -= 1) if (posted[i]!.line.category === 'NetPayClearing') posted.splice(i, 1);
      }
    }

    // Option blockers, one per code with counts, groups and (only for holders of the per-employee permissions) employee numbers: never names.
    const refsOf = (items: { line: InternalSource }[]) => [...new Set(items.map((b) => b.line.employeeReference ?? ''))].filter(Boolean).sort();
    const addEmployees = (items: { line: InternalSource }[]) => (caps.canViewPerEmployee ? ` Employees: ${refsOf(items).join(', ')}.` : '');
    const blockedBy = (code: OptionBlockerCode) => optionBlocked.filter((b) => b.code === code);
    const dimensionName = DIMENSION_NAMES[options.wageGroupDimension ?? 'Department'].toLowerCase();
    const defaultWage = blockedBy('DefaultWageAccountRequired');
    if (defaultWage.length > 0) blockers.push({ code: 'DefaultWageAccountRequired', message: 'Single account mode posts all wages to the company default Wage Expense account, and no active company-default Gross wages mapping exists.' });
    if (blockedBy('WageGroupDimensionRequired').length > 0) blockers.push({ code: 'WageGroupDimensionRequired', message: 'By group mode needs a group dimension (department, work location, role, discipline, employment type or employee group).' });
    const missingMapping = blockedBy('GroupMappingMissing');
    if (missingMapping.length > 0) {
      const byLabel = new Map<string, Set<string>>();
      for (const b of missingMapping) byLabel.set(b.label, (byLabel.get(b.label) ?? new Set()).add(b.line.employeeId));
      const groups = [...byLabel.entries()].map(([label, ids]) => `${label} (${ids.size})`).join(', ');
      blockers.push({ code: 'GroupMappingMissing', message: `${new Set(missingMapping.map((b) => b.line.employeeId)).size} employee(s) are in ${dimensionName} groups with no wage account mapping: ${groups}.${addEmployees(missingMapping)}` });
    }
    const noMembership = blockedBy('GroupMembershipMissing');
    if (noMembership.length > 0) blockers.push({ code: 'GroupMembershipMissing', message: `${new Set(noMembership.map((b) => b.line.employeeId)).size} employee(s) have no ${dimensionName} value, so their wages cannot be assigned to a group.${addEmployees(noMembership)}` });
    const ambiguousGroup = blockedBy('EmployeeGroupAmbiguous');
    if (ambiguousGroup.length > 0) blockers.push({ code: 'EmployeeGroupAmbiguous', message: `${new Set(ambiguousGroup.map((b) => b.line.employeeId)).size} employee(s) are in more than one active employee group on the accounting date.${addEmployees(ambiguousGroup)}` });

    // Unmapped and excluded tables.
    const unmappedGroups = new Map<string, UnmappedItem>();
    for (const { line, reason } of unmappedLines) {
      const groupKey = `${line.category}|${line.subcategory}|${line.payrollCode}|${reason}`;
      const group = unmappedGroups.get(groupKey) ?? { category: line.category, subcategory: line.subcategory, payrollCode: line.payrollCode, lineCount: 0, amountCents: 0, reason };
      group.lineCount += 1;
      group.amountCents += line.amountCents;
      unmappedGroups.set(groupKey, group);
    }
    const unmapped = [...unmappedGroups.values()];
    if (unmapped.length > 0) blockers.push({ code: 'UnmappedLine', message: `${unmapped.reduce((n, u) => n + u.lineCount, 0)} payroll line(s) have no usable GL mapping. Map them in the GL Mapping tab, then preview again.` });

    const excludedGroups = new Map<string, ExcludedItem>();
    for (const { line, mapping } of excludedLines) {
      const groupKey = `${line.category}|${mapping.id}`;
      const group = excludedGroups.get(groupKey) ?? { category: line.category, amountCents: 0, reason: mapping.nonPostingReason ?? 'Recorded as non-posting', mappingId: mapping.id };
      group.amountCents += line.amountCents;
      excludedGroups.set(groupKey, group);
    }
    const excluded = [...excludedGroups.values()];
    const excludedDebits = sumCents(excludedLines.filter((x) => x.line.side === 'Debit').map((x) => x.line.amountCents));
    const excludedCredits = sumCents(excludedLines.filter((x) => x.line.side === 'Credit').map((x) => x.line.amountCents));
    if (excludedDebits !== excludedCredits) blockers.push({ code: 'ExcludedUnbalances', message: 'Excluding this line would unbalance the entry. A non-posting rule is only valid for items whose offsetting side is also non-posting.' });

    // Journal lines: compound aggregates by account and category; per-employee keeps each employee's lines. With the per-employee net pay option only the
    // net pay line is kept per employee; every other line stays consolidated.
    const perEmployeeNetPay = !legacy && mode === 'Compound' && options.netPayDetailMode === 'PerEmployee';
    const groups = new Map<string, JournalLine & { _cat: number }>();
    for (const p of posted) {
      const empRef = mode === 'PerEmployee' || (perEmployeeNetPay && p.line.category === 'NetPayClearing') ? p.line.employeeReference : null;
      const group = p.res.group;
      const groupLine = group;
      const lineKey = [p.side, p.mapping.accountNumber, p.line.category, p.line.subcategory, p.line.payrollCode, p.mapping.departmentId, p.mapping.locationCode, empRef, groupLine?.key ?? '', p.res.level].join('|');
      let description = p.line.description + (empRef ? ` (${empRef})` : '');
      if (groupLine) description += ` - ${DIMENSION_NAMES[groupLine.dimension]}: ${groupLine.label}`;
      if (consolidated && p.line.category === 'NetPayClearing') description = `Net Pay - Payroll Run ${reference} - ${run.checkDate}`;
      if (p.flipped) description += ' (negative source amount, posted on the opposite side)';
      const existing = groups.get(lineKey);
      if (existing) {
        existing.amountCents += p.amountCents;
        existing.sourceLineIds.push(p.line.id);
      } else {
        const isWage = p.line.category === 'EarningsGross';
        groups.set(lineKey, {
          lineNumber: 0, side: p.side, amountCents: p.amountCents, accountNumber: p.mapping.accountNumber ?? '', accountName: p.mapping.accountName ?? '', accountType: p.mapping.accountType ?? '',
          category: p.line.category, subcategory: p.line.subcategory, payrollCode: p.line.payrollCode, employeeReference: empRef, department: p.mapping.departmentId, location: p.mapping.locationCode,
          classDimension: groupLine?.label ?? null, checkReference: null, description, mappingId: p.mapping.id, mappingVersion: p.mapping.mappingVersion, sourceLineIds: [p.line.id], _cat: CATEGORY_ORDER.get(p.line.category) ?? 99,
          groupDimension: groupLine?.dimension ?? null, groupKey: groupLine?.key ?? null, groupLabel: groupLine?.label ?? null,
          resolutionLevel: isWage && options.recorded ? p.res.level : null, resolutionReason: isWage && options.recorded ? p.res.reason : null,
        });
      }
    }
    const ordered = [...groups.values()].sort((a, b) =>
      (a.side === b.side ? 0 : a.side === 'Debit' ? -1 : 1) || a._cat - b._cat || (a.subcategory ?? '').localeCompare(b.subcategory ?? '') || a.accountNumber.localeCompare(b.accountNumber)
      || (a.groupLabel ?? '').localeCompare(b.groupLabel ?? '') || (a.employeeReference ?? '').localeCompare(b.employeeReference ?? ''));
    const lines: JournalLine[] = ordered.map(({ _cat, ...rest }, i) => { void _cat; return { ...rest, lineNumber: i + 1 }; });

    const totalDebits = sumCents(lines.filter((l) => l.side === 'Debit').map((l) => l.amountCents));
    const totalCredits = sumCents(lines.filter((l) => l.side === 'Credit').map((l) => l.amountCents));
    const difference = totalDebits - totalCredits;
    const isBalanced = difference === 0 && lines.length > 0;

    // Completeness: every source amount is posted, excluded, unmapped or blocked by a mapping option; nothing is dropped.
    const mappingBlocked = unmapped.length > 0 || optionBlocked.length > 0;
    const sourceTotal = sumCents(source.map((s) => Math.abs(s.amountCents)));
    const accounted = sumCents(posted.map((p) => p.amountCents)) + sumCents(excludedLines.map((e) => Math.abs(e.line.amountCents))) + sumCents(unmappedLines.map((u) => Math.abs(u.line.amountCents)))
      + sumCents(optionBlocked.map((b) => Math.abs(b.line.amountCents)));
    const unmappedAmount = sumCents(unmappedLines.map((u) => u.line.amountCents)) + sumCents(optionBlocked.map((b) => b.line.amountCents));
    const excludedAmount = sumCents(excludedLines.map((e) => e.line.amountCents));
    const reconDifference = sourceTotal - accounted;
    const isComplete = reconDifference === 0 && !mappingBlocked;
    if (!mappingBlocked && !isBalanced) blockers.push({ code: 'Unbalanced', message: `Total debits and total credits are not equal (difference ${formatCents(difference)}). The export is blocked until the entry balances exactly.` });
    if (!mappingBlocked && reconDifference !== 0) blockers.push({ code: 'Incomplete', message: 'Some payroll amounts are in neither the journal nor the excluded list. The export is blocked until every payroll line is accounted for.' });

    // Account-type guidance (a warning, never a blocker).
    const warned = new Set<string>();
    for (const p of posted) {
      const type = p.mapping.accountType;
      const message = EXPENSE_CATEGORIES.has(p.line.category) && type && type !== 'Expense'
        ? `${categoryLabel(p.line.category)} is mapped to a ${type.toLowerCase()} account (${p.mapping.accountNumber}); an expense account is usual.`
        : LIABILITY_CATEGORIES.has(p.line.category) && type === 'Expense'
          ? `${categoryLabel(p.line.category)} is mapped to an expense account (${p.mapping.accountNumber}); a liability account is usual.`
          : null;
      if (message && !warned.has(message)) {
        warned.add(message);
        warnings.push({ code: 'MappingWarning', message });
      }
    }
    // Deduction codes without a mapping only warn (counts only), and never block. A legacy request behaves exactly as before, so it carries no such warning.
    const codesWithoutMapping = legacy || this.scenario() === 'codes-unavailable' ? 0 : this.codeItems().filter((c) => c.kind === 'Deduction' && c.mappingStatus === 'Unmapped').length;
    if (codesWithoutMapping > 0) {
      warnings.push({ code: 'CodesWithoutMapping', message: `${codesWithoutMapping} active deduction code(s) have no GL mapping.` });
    }

    const gross = sumCents(calcs.map((x) => x.grossCents));
    const employeeTaxes = sumCents(calcs.flatMap((x) => x.taxLines.filter((t) => !t.isEmployerPaid).map((t) => t.amountCents)));
    const employerTaxes = sumCents(calcs.flatMap((x) => x.taxLines.filter((t) => t.isEmployerPaid).map((t) => t.amountCents)));
    const postedIds = new Set(lines.flatMap((l) => l.sourceLineIds));

    const existing = this.exports.find((e) => e.key === key);
    const employees: PreviewEmployee[] = calcs.map((x) => {
      const emp = MOCK_EMPLOYEES[x.employeeId]!;
      return {
        employeeId: emp.id, employeeReference: emp.reference, displayName: caps.canViewPerEmployee ? emp.displayName : null, department: emp.department, location: x.stateCode,
        grossEarningsCents: x.grossCents, netPayCents: x.netPayCents,
      };
    });

    const sourceLines: SourceLine[] = source.map((s) => ({
      id: s.id, calculationId: s.calculationId, employeeReference: s.employeeReference, category: s.category, subcategory: s.subcategory,
      payrollCode: s.payrollCode, jurisdiction: s.jurisdiction, side: s.side, amountCents: s.amountCents, provenance: s.provenance, posted: postedIds.has(s.id),
    }));
    const coverage: SourceCoverageItem[] = CATEGORIES.map((cat) => ({ category: cat.key, status: COVERAGE[cat.key]!.status, note: COVERAGE[cat.key]!.note }));

    // ---- the breakdowns behind the journal (contract section 6) ----
    const accountOf = (category: string, payrollCode?: string | null): AccountRef | null => {
      const p = posted.find((x) => x.line.category === category && (payrollCode === undefined || x.line.payrollCode === payrollCode));
      return p ? { accountNumber: p.mapping.accountNumber ?? '', accountName: p.mapping.accountName ?? '' } : null;
    };
    const taxes = new Map<string, BreakdownLine>();
    for (const s of source.filter((x) => x.category === 'EmployeeTaxWithholding' || x.category === 'EmployerTaxExpense')) {
      const employer = s.category === 'EmployerTaxExpense';
      const taxKey = `${s.category}|${s.payrollCode ?? s.subcategory ?? ''}`;
      const row = taxes.get(taxKey) ?? {
        key: taxKey, label: `${subcategoryLabel(s.subcategory ?? 'Other', s.payrollCode)} (${employer ? 'employer' : 'employee'})`, amountCents: 0,
        account: accountOf(s.category, s.payrollCode), note: null,
      };
      row.amountCents += s.amountCents;
      taxes.set(taxKey, row);
    }
    const deductions: BreakdownLine[] = ['PreTaxDeductionPayable', 'PostTaxDeductionPayable', 'GarnishmentPayable']
      .map((category) => ({
        key: category, label: categoryLabel(category), amountCents: sumCents(source.filter((s) => s.category === category).map((s) => s.amountCents)), account: accountOf(category), note: null,
      }))
      .filter((d) => d.amountCents !== 0);

    const wageGroups = legacy || options.wageMappingMode === 'LegacyResolver' ? [] : this.wageGroupLines(source, accountingDate, options);
    const unusedCount = legacy ? 0 : this.unusedWageMappingCount(accountingDate, options);
    const missingMappings = unmapped.filter((u) => u.reason !== 'Ambiguous').length + new Set(optionBlocked.map((b) => `${b.code}|${b.label}`)).size;
    const ambiguousMappings = unmapped.filter((u) => u.reason === 'Ambiguous').length;
    const catalogAvailable = this.scenario() !== 'codes-unavailable';

    return {
      payrollRunId: run.id, mode, accountingDate, status: blockers.length === 0 ? 'Ready' : 'ValidationFailed', currency: 'USD', employees,
      journal: { reference, totalDebitsCents: totalDebits, totalCreditsCents: totalCredits, differenceCents: difference, isBalanced, lines },
      reconciliation: {
        finalizedGrossCents: gross, employeeTaxesCents: employeeTaxes, employerTaxesCents: employerTaxes, preTaxDeductionsCents: sumCents(calcs.map((x) => x.preTaxCents)),
        postTaxDeductionsCents: sumCents(calcs.map((x) => x.postTaxCents)), garnishmentsCents: sumCents(calcs.map((x) => x.garnishmentsCents)), employerContributionsCents: null,
        netPayCents: sumCents(calcs.map((x) => x.netPayCents)), totalPayrollCostCents: gross + employerTaxes, journalDebitsCents: totalDebits, journalCreditsCents: totalCredits,
        unmappedAmountCents: unmappedAmount, excludedAmountCents: excludedAmount, differenceCents: reconDifference, isComplete,
      },
      sourceLines, unmapped, excluded, warnings, blockers, sourceCoverage: coverage,
      existingExport: existing ? { id: existing.detail.id, status: existing.detail.status } : null,
      options, configuration: { options, mappingVersionMax: this.mappingVersionCounter, exportKeyVersion: legacy ? 1 : 2 },
      summary: {
        employeeCount: calcs.length, groupCount: wageGroups.filter((g) => g.groupKey !== '' && !g.groupKey.startsWith('__')).length,
        earningCodeCount: catalogAvailable ? MOCK_CODES.filter((c) => c.kind === 'Earning').length : 0, deductionCodeCount: catalogAvailable ? MOCK_CODES.filter((c) => c.kind === 'Deduction').length : 0,
        mappedComponents: new Set(posted.map((p) => p.line.category)).size, missingMappings, ambiguousMappings, totalDebitsCents: totalDebits, totalCreditsCents: totalCredits, varianceCents: difference,
      },
      wageGroups, taxesByType: [...taxes.values()], deductionsByCategory: deductions, employerContributions: { notSupplied: true, items: [] },
      netPayDetail: consolidated && caps.canViewPerEmployee && !blockers.some((b) => b.code === 'NetPayNotConsolidatable')
        ? calcs.map((x) => ({ employeeReference: MOCK_EMPLOYEES[x.employeeId]!.reference, netPayCents: x.netPayCents })).sort((a, b) => a.employeeReference.localeCompare(b.employeeReference))
        : null,
      unusedWageMappings: legacy ? null : { count: unusedCount, note: unusedCount > 0 ? 'Wage mappings for an employee, group, department or location that this mode does not use. They are kept and have no effect on this journal.' : null },
      limitations: [
        'Earning types (regular, overtime, bonus, commission) are not split: the payroll supplies one gross wage amount per employee.',
        'Group attributes are the employee\'s current values, not the values on the check date.',
        'Check numbers are not supplied by the payroll, so the check reference stays empty.',
        'Deductions are posted as three totals by category: per-code amounts are not supplied by the payroll.',
      ],
      _lines: lines,
    };
  }

  /** One row per group the payroll's wage lines fall into (or one row for the single account), with the account and the reason in words. */
  private wageGroupLines(source: InternalSource[], date: string, options: ExportOptions): WageGroupLine[] {
    const rows = new Map<string, WageGroupLine>();
    const wageLines = source.filter((s) => s.category === 'EarningsGross');
    for (const line of wageLines) {
      const res = this.resolveLine(line, date, options);
      const value = options.wageMappingMode === 'GroupBased' && options.wageGroupDimension ? this.dimensionValue(options.wageGroupDimension, line.employeeId, date) : null;
      let groupKey = '';
      let label = 'All employees';
      if (res.kind === 'mapped' && res.level === 'EmployeeOverride') {
        groupKey = '__override';
        label = 'Employee-specific overrides';
      } else if (value) {
        groupKey = value.kind === 'value' ? value.key : value.kind === 'none' ? '' : '__ambiguous';
        label = value.kind === 'value' ? value.label : value.kind === 'none' ? `No ${DIMENSION_NAMES[options.wageGroupDimension!].toLowerCase()}` : 'In more than one group';
      }
      const row = rows.get(groupKey) ?? { groupKey, label, employeeCount: 0, grossAmountCents: 0, account: null, mappingId: null, mappingVersion: null, reason: null };
      row.grossAmountCents += line.amountCents;
      if (res.kind === 'mapped') {
        row.account = { accountNumber: res.mapping.accountNumber ?? '', accountName: res.mapping.accountName ?? '' };
        row.mappingId = res.mapping.id;
        row.mappingVersion = res.mapping.mappingVersion;
        row.reason = res.reason;
      } else if (res.kind === 'blocked') {
        row.reason = BLOCKED_REASON[res.code];
      } else {
        row.reason = 'Two equally specific mappings apply.';
      }
      rows.set(groupKey, row);
    }
    for (const row of rows.values()) row.employeeCount = new Set(wageLines.filter((l) => this.rowKeyOf(l, date, options) === row.groupKey).map((l) => l.employeeId)).size;
    return [...rows.values()].sort((a, b) => a.label.localeCompare(b.label));
  }

  private rowKeyOf(line: InternalSource, date: string, options: ExportOptions): string {
    const res = this.resolveLine(line, date, options);
    if (res.kind === 'mapped' && res.level === 'EmployeeOverride') return '__override';
    if (options.wageMappingMode === 'GroupBased' && options.wageGroupDimension) {
      const value = this.dimensionValue(options.wageGroupDimension, line.employeeId, date);
      return value.kind === 'value' ? value.key : value.kind === 'none' ? '' : '__ambiguous';
    }
    return '';
  }

  /** Wage mappings that the chosen mode does not use (employee, group, department or location scoped): listed so nobody wonders why they have no effect. */
  private unusedWageMappingCount(date: string, options: ExportOptions): number {
    const dim = options.wageMappingMode === 'GroupBased' ? options.wageGroupDimension : null;
    return this.activeMappings(date).filter((m) => m.category === 'EarningsGross' && m.treatment === 'Post' && (
      options.wageMappingMode === 'SingleAccount'
        ? m.employeeId !== null || m.departmentId !== null || m.locationCode !== null || m.groupDimension !== null
        : options.wageMappingMode === 'GroupBased'
          ? m.locationCode !== null || (m.departmentId !== null && dim !== 'Department') || (m.groupDimension !== null && m.groupDimension !== dim)
          : false
    )).length;
  }

  private versionKey(run: MockRun, version: number): string {
    return sha256Hex((run.versions[version] ?? []).map((c) => c.calculationId).sort().join(','));
  }

  /** ExportKey v1 (the unchanged formula) for the legacy defaults; otherwise v2, which adds the options, the highest mapping version and the group configuration version. */
  private keyFor(run: MockRun, version: number, mode: ExportMode, accountingDate: string, kind: ExportKind, reversalOfId: string | null, replacesId: string | null, options: ExportOptions): string {
    const v1 = [MOCK_COMPANY_ID, run.id, this.versionKey(run, version), 'Csv', mode, accountingDate, kind, reversalOfId ?? '', replacesId ?? ''];
    if (options.source === 'LegacyDefault') return sha256Hex(v1.join('|'));
    return sha256Hex([...v1, 'v2', options.netPayDetailMode ?? '', options.wageMappingMode ?? '', options.wageGroupDimension ?? '', String(this.mappingVersionCounter), String(options.groupConfigVersion ?? 0)].join('|'));
  }

  /** What an export made before options existed carries: the legacy values, flagged as not recorded. */
  private legacyRecord(mode: ExportMode): ExportOptions {
    return { ...this.legacyOptions(mode), recorded: false };
  }

  // ---------------------------------------------------------------- preview and create

  previewExport(params: PreviewParams): ExportPreview {
    this.require('canPreview', 'preview accounting exports');
    if (params.mode === 'PerEmployee' && !this.getCapabilities().canViewPerEmployee) {
      throw apiError(403, 'Per-employee detail needs the employee compensation permission.', ExportErrorCode.PerEmployeeNotPermitted);
    }
    const options = this.effectiveOptions(params, params.mode);
    this.requirePerEmployeeFor(params.mode, options);
    const run = this.requireRun(params.payrollRunId);
    const { _lines, ...preview } = this.evaluate(run, run.currentVersion, params.mode, params.accountingDate || run.checkDate, 'Original', null, null, options);
    void _lines;
    return preview;
  }

  createExport(input: CreateExportInput): CreateExportResult {
    this.require('canGenerate', 'generate accounting exports');
    if (input.mode === 'PerEmployee' && !this.getCapabilities().canViewPerEmployee) {
      throw apiError(403, 'Per-employee detail needs the employee compensation permission.', ExportErrorCode.PerEmployeeNotPermitted);
    }
    const options = this.effectiveOptions(input, input.mode);
    this.requirePerEmployeeFor(input.mode, options);
    const run = this.requireRun(input.payrollRunId);
    const date = input.accountingDate || run.checkDate;
    const key = this.keyFor(run, run.currentVersion, input.mode, date, 'Original', null, null, options);
    const existing = this.exports.find((e) => e.key === key);
    if (existing) {
      this.audit(existing, 'AccountingExportDuplicateRequested', { exportId: existing.detail.id });
      return { export: structuredClone(existing.detail), duplicate: true };
    }
    const { _lines, ...report } = this.evaluate(run, run.currentVersion, input.mode, date, 'Original', null, null, options);
    void _lines;
    if (report.status !== 'Ready') throw new ExportValidationError(report);
    if (this.scenario() === 'failed') {
      const failed = this.build(run, run.currentVersion, input.mode, date, 'Original', null, null, null, false, true, undefined, undefined, options);
      void failed;
      throw apiError(500, 'The export file could not be generated.', 'EXPORT_GENERATION_FAILED');
    }
    const stored = this.build(run, run.currentVersion, input.mode, date, 'Original', null, null, null, false, false, undefined, undefined, options);
    return { export: structuredClone(stored.detail), duplicate: false };
  }

  /** Persists an export (the only place an export is created): lines, checksum and the first state change. */
  private build(run: MockRun, version: number, mode: ExportMode, date: string, kind: ExportKind, reversalOfId: string | null, replacesId: string | null, reason: string | null, seeding: boolean, failed = false, lineOverride?: JournalLine[], reversalReference?: string, options: ExportOptions = this.legacyOptions(mode)): StoredExport {
    const evaluated = this.evaluate(run, version, mode, date, kind, reversalOfId, replacesId, options);
    const key = this.keyFor(run, version, mode, date, kind, reversalOfId, replacesId, options);
    const lines = lineOverride ?? evaluated._lines;
    const id = seeding ? `mock-export-${run.id}` : crypto.randomUUID();
    const reference = reversalOfId ? `PAYREV-${date.replace(/-/g, '')}-${key.slice(0, 8).toUpperCase()}` : evaluated.journal!.reference;
    const header = {
      id, companyId: MOCK_COMPANY_ID, payrollRunId: run.id, kind, mode, periodStart: run.periodStart, periodEnd: run.periodEnd, checkDate: run.checkDate, accountingDate: date,
      journalReference: reference, reversalReference: reversalReference ?? null,
    };
    const csv = failed ? '' : buildCsv(header, lines);
    const generatedAt = this.now();
    const status: ExportStatus = failed ? 'Failed' : 'Generated';
    const detail: ExportDetail = {
      id, payrollRunId: run.id, kind, mode, targetFormat: 'Csv', status, checkDate: run.checkDate, periodStart: run.periodStart, periodEnd: run.periodEnd, accountingDate: date,
      journalReference: reference, totalDebitsCents: sumCents(lines.filter((l) => l.side === 'Debit').map((l) => l.amountCents)),
      totalCreditsCents: sumCents(lines.filter((l) => l.side === 'Credit').map((l) => l.amountCents)), lineCount: lines.length, checksum: failed ? '' : sha256Hex(csv),
      csvSchemaVersion: CSV_SCHEMA_VERSION, mappingVersionMax: lines.reduce((m, l) => Math.max(m, l.mappingVersion), 0), reversalOfExportId: reversalOfId, replacesExportId: replacesId,
      reversedByExportId: null, supersededByExportId: null, reason, generatedAtUtc: generatedAt, downloadCount: 0, firstDownloadedAtUtc: null, operationId: seeding ? `mock-op-${run.id}` : crypto.randomUUID(),
      lines: structuredClone(lines), stateHistory: [{ fromStatus: null, toStatus: status, reason: failed ? 'The CSV could not be generated.' : null, occurredAtUtc: generatedAt }],
      options: structuredClone(options),
    };
    const stored: StoredExport = { detail, key, payrollVersion: version, reversalReference: reversalReference ?? null, audit: [] };
    this.exports.push(stored);
    this.audit(stored, failed ? 'AccountingExportFailed' : 'AccountingExportGenerated', {
      exportId: id, status, lineCount: lines.length, totalDebits: Number(plainDecimal(detail.totalDebitsCents)), totalCredits: Number(plainDecimal(detail.totalCreditsCents)), mode, kind,
      ...(options.recorded ? { netPayDetailMode: options.netPayDetailMode, wageMappingMode: options.wageMappingMode, wageGroupDimension: options.wageGroupDimension, optionSource: options.source, settingsVersion: options.settingsVersion, groupConfigVersion: options.groupConfigVersion } : {}),
    });
    return stored;
  }

  private audit(stored: StoredExport, eventType: string, metadata: Record<string, unknown>): void {
    stored.audit.unshift({ id: this.id('audit'), occurredAtUtc: this.now(), actorUserId: 'mock-user', actorType: 'User', actorEmailSnapshot: null, eventType, metadataJson: JSON.stringify({ ...metadata, operationId: stored.detail.operationId }) });
  }

  private transition(stored: StoredExport, to: ExportStatus, reason: string | null): void {
    stored.detail.stateHistory.push({ fromStatus: stored.detail.status, toStatus: to, reason, occurredAtUtc: this.now() });
    stored.detail.status = to;
  }

  // ---------------------------------------------------------------- list, detail, download

  listExports(params?: ListExportsParams): Paged<ExportSummary> {
    this.require('canPreview', 'view accounting exports');
    const rows = this.exports
      .filter((e) => (!params?.runId || e.detail.payrollRunId === params.runId) && (!params?.status || e.detail.status === params.status) && (!params?.kind || e.detail.kind === params.kind))
      .sort((a, b) => (a.detail.generatedAtUtc < b.detail.generatedAtUtc ? 1 : a.detail.generatedAtUtc > b.detail.generatedAtUtc ? -1 : 0));
    const pageSize = params?.pageSize ?? 50;
    const page = params?.page ?? 1;
    const items = rows.slice((page - 1) * pageSize, page * pageSize).map((e) => {
      const { lines, stateHistory, ...summary } = e.detail;
      void lines; void stateHistory;
      return structuredClone(summary);
    });
    return { items, page, pageSize, totalCount: rows.length };
  }

  private find(id: string): StoredExport {
    const stored = this.exports.find((e) => e.detail.id === id);
    if (!stored) throw apiError(404, 'That export was not found.', ExportErrorCode.NotFound);
    return stored;
  }

  private requirePerEmployeeAccess(stored: StoredExport): void {
    if (stored.detail.mode === 'PerEmployee' && !this.getCapabilities().canViewPerEmployee) {
      throw apiError(403, 'Per-employee detail needs the employee compensation permission.', ExportErrorCode.PerEmployeeNotPermitted);
    }
  }

  getExport(id: string): ExportDetail {
    this.require('canPreview', 'view accounting exports');
    const stored = this.find(id);
    this.requirePerEmployeeAccess(stored);
    return structuredClone(stored.detail);
  }

  private markDownloaded(stored: StoredExport): void {
    stored.detail.downloadCount += 1;
    if (stored.detail.firstDownloadedAtUtc === null) stored.detail.firstDownloadedAtUtc = this.now();
    if (stored.detail.status === 'Generated') this.transition(stored, 'Downloaded', null);
    this.audit(stored, 'AccountingExportDownloaded', { exportId: stored.detail.id, downloadCount: stored.detail.downloadCount });
  }

  downloadExport(id: string): DownloadedExport {
    this.require('canPreview', 'download accounting exports');
    const stored = this.find(id);
    this.requirePerEmployeeAccess(stored);
    if (stored.detail.status === 'Failed') throw apiError(409, 'This export failed and has no file.', ExportErrorCode.InvalidTransition);
    const d = stored.detail;
    const csv = buildCsv({
      id: d.id, companyId: MOCK_COMPANY_ID, payrollRunId: d.payrollRunId, kind: d.kind, mode: d.mode, periodStart: d.periodStart, periodEnd: d.periodEnd, checkDate: d.checkDate,
      accountingDate: d.accountingDate, journalReference: d.journalReference, reversalReference: stored.reversalReference,
    }, d.lines);
    if (sha256Hex(csv) !== d.checksum) throw apiError(500, 'The stored export failed its integrity check, so nothing was served.', ExportErrorCode.IntegrityError);
    this.markDownloaded(stored);
    return { blob: new Blob([csv], { type: 'text/csv;charset=utf-8' }), fileName: `payroll-journal-${d.journalReference}.csv` };
  }

  // ---------------------------------------------------------------- lifecycle

  private syncOne(stored: StoredExport): void {
    if (stored.detail.kind === 'Reversal') return;
    const run = this.requireRun(stored.detail.payrollRunId);
    if ((stored.detail.status === 'Generated' || stored.detail.status === 'Downloaded') && stored.payrollVersion !== run.currentVersion) {
      this.transition(stored, 'ReversalRequired', 'The payroll was corrected or reversed after this export.');
      this.audit(stored, 'AccountingExportReversalRequired', { exportId: stored.detail.id });
    }
  }

  syncPayrollStatus(id: string): ExportDetail {
    this.require('canGenerate', 'check payroll status');
    const stored = this.find(id);
    this.syncOne(stored);
    return structuredClone(stored.detail);
  }

  reverseExport(id: string, reason: string): ExportDetail {
    this.require('canReverse', 'generate a reversal');
    const original = this.find(id);
    const trimmed = (reason ?? '').trim();
    if (!trimmed) throw apiError(400, 'A reason is required to generate a reversal.', 'REASON_REQUIRED');
    if (trimmed.length > 500) throw apiError(400, 'The reason must be 500 characters or fewer.', 'REASON_REQUIRED');
    const status = original.detail.status;
    if (original.detail.kind === 'Reversal' || !(status === 'Generated' || status === 'Downloaded' || status === 'ReversalRequired')) {
      throw apiError(409, `An export that is ${status.toLowerCase()} cannot be reversed.`, ExportErrorCode.InvalidTransition);
    }
    const run = this.requireRun(original.detail.payrollRunId);
    const swapped: JournalLine[] = original.detail.lines.map((l) => ({
      ...l, side: l.side === 'Debit' ? 'Credit' : 'Debit', description: `Reversal: ${l.description}`, sourceLineIds: [...l.sourceLineIds],
    }));
    const reversal = this.build(run, original.payrollVersion, original.detail.mode, original.detail.accountingDate, 'Reversal', original.detail.id, null, trimmed, false, false, swapped, original.detail.journalReference, original.detail.options);
    original.detail.reversedByExportId = reversal.detail.id;
    this.transition(original, 'Reversed', trimmed);
    this.audit(original, 'AccountingExportReversed', { exportId: original.detail.id, reversalExportId: reversal.detail.id });
    return structuredClone(reversal.detail);
  }

  replaceExport(id: string, input: ReplacementInput): ExportDetail {
    this.require('canReverse', 'generate a replacement');
    const original = this.find(id);
    const reason = (input.reason ?? '').trim();
    if (!reason) throw apiError(400, 'A reason is required to generate a replacement.', 'REASON_REQUIRED');
    if (original.detail.status !== 'Reversed') throw apiError(409, 'Only a reversed export can be replaced. Generate the reversal first.', ExportErrorCode.InvalidTransition);
    const run = this.requireRun(original.detail.payrollRunId);
    if (run.currentVersion === original.payrollVersion) throw apiError(409, 'The payroll has not changed since this export, so there is nothing to replace it with.', ExportErrorCode.PayrollUnchanged);
    const date = input.accountingDate || original.detail.accountingDate;
    // A replacement re-runs the pipeline with the ORIGINAL's recorded options, not today's company defaults. A legacy original replays the original rules.
    const options = original.detail.options.recorded ? original.detail.options : this.legacyOptions(original.detail.mode);
    const { _lines, ...report } = this.evaluate(run, run.currentVersion, original.detail.mode, date, 'Replacement', null, original.detail.id, options);
    void _lines;
    if (report.status !== 'Ready') throw new ExportValidationError(report);
    const replacement = this.build(run, run.currentVersion, original.detail.mode, date, 'Replacement', null, original.detail.id, reason, false, false, undefined, undefined, options);
    original.detail.supersededByExportId = replacement.detail.id;
    this.transition(original, 'Superseded', reason);
    this.audit(original, 'AccountingExportSuperseded', { exportId: original.detail.id, replacementExportId: replacement.detail.id });
    return structuredClone(replacement.detail);
  }

  getExportAudit(id: string): ExportAuditItem[] {
    this.require('canViewAudit', 'view the export audit history');
    return structuredClone(this.find(id).audit);
  }

  // ---------------------------------------------------------------- chart of accounts

  listChartAccounts(params?: ListChartAccountsParams): Paged<ChartAccount> {
    if (!this.getCapabilities().canPreview) throw apiError(403, 'You do not have permission to view the chart of accounts.');
    this.recomputeUseCounts();
    const term = (params?.search ?? '').trim().toLowerCase();
    const rows = this.chart
      .filter((a) => (!term || a.accountNumber.toLowerCase().includes(term) || a.name.toLowerCase().includes(term)) && (!params?.type || a.accountType === params.type) && (params?.active === undefined || a.isActive === params.active))
      .sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
    const pageSize = params?.pageSize ?? 50;
    const page = params?.page ?? 1;
    return { items: structuredClone(rows.slice((page - 1) * pageSize, page * pageSize)), page, pageSize, totalCount: rows.length };
  }

  private validateAccount(number: string, name: string, type: string, excludeId?: string): string | null {
    if (!number.trim()) return 'Enter an account number.';
    if (number.trim().length > 30) return 'The account number must be 30 characters or fewer.';
    if (!name.trim()) return 'Enter an account name.';
    if (name.trim().length > 150) return 'The account name must be 150 characters or fewer.';
    if (!CHART_ACCOUNT_TYPES.includes(type as ChartAccountType)) return 'Choose an account type.';
    if (this.chart.some((a) => a.id !== excludeId && a.accountNumber.toLowerCase() === number.trim().toLowerCase())) return `Account number ${number.trim()} already exists.`;
    return null;
  }

  createChartAccount(input: CreateChartAccountInput): ChartAccount {
    this.require('canManageChartOfAccounts', 'manage the chart of accounts');
    const problem = this.validateAccount(input.accountNumber, input.name, input.accountType);
    if (problem) throw apiError(problem.includes('already exists') ? 409 : 400, problem, problem.includes('already exists') ? ExportErrorCode.AccountNumberExists : ExportErrorCode.MappingInvalid);
    const account: ChartAccount = {
      id: crypto.randomUUID(), accountNumber: input.accountNumber.trim(), name: input.name.trim(), accountType: input.accountType, isActive: true, source: 'Manual',
      externalReference: input.externalReference?.trim() || null, mappingUseCount: 0,
    };
    this.chart.push(account);
    return structuredClone(account);
  }

  updateChartAccount(id: string, input: UpdateChartAccountInput): ChartAccount {
    this.require('canManageChartOfAccounts', 'manage the chart of accounts');
    const account = this.chart.find((a) => a.id === id);
    if (!account) throw apiError(404, 'That account was not found.');
    if (!input.name.trim()) throw apiError(400, 'Enter an account name.', ExportErrorCode.MappingInvalid);
    account.name = input.name.trim();
    if (input.externalReference !== undefined) account.externalReference = input.externalReference?.trim() || null;
    // A mapping keeps the name it was saved with (history never changes), so mappings are not touched here.
    return structuredClone(account);
  }

  setAccountActive(id: string, active: boolean): void {
    this.require('canManageChartOfAccounts', 'manage the chart of accounts');
    const account = this.chart.find((a) => a.id === id);
    if (!account) throw apiError(404, 'That account was not found.');
    account.isActive = active;
  }

  importChartAccounts(csv: string, dryRun: boolean): ChartImportResult {
    this.require('canManageChartOfAccounts', 'manage the chart of accounts');
    if (csv.length > 1024 * 1024) throw apiError(400, 'The file is larger than 1 MB.', ExportErrorCode.ChartImportInvalid);
    const rows = csv.split(/\r\n|\n|\r/).filter((l, i, all) => l.trim() !== '' || i < all.length - 1);
    const header = (rows[0] ?? '').split(',').map((h) => h.trim().toLowerCase());
    if (header.join(',') !== 'accountnumber,name,accounttype') {
      throw apiError(400, 'The first row must be the header: accountNumber,name,accountType.', ExportErrorCode.ChartImportInvalid);
    }
    const data = rows.slice(1);
    if (data.length > 5000) throw apiError(400, 'A file can hold at most 5,000 accounts.', ExportErrorCode.ChartImportInvalid);
    const errors: { row: number; message: string }[] = [];
    const seen = new Set<string>();
    data.forEach((line, i) => {
      const row = i + 2; // the file's own line number: the header is line 1
      const cells = line.split(',').map((x) => x.trim());
      const [number = '', name = '', type = ''] = cells;
      const matchedType = CHART_ACCOUNT_TYPES.find((t) => t.toLowerCase() === type.toLowerCase());
      const problem = cells.length !== 3 ? 'Each row needs exactly three values: account number, name and type.' : this.validateAccount(number, name, matchedType ?? '');
      if (problem) errors.push({ row, message: problem });
      else if (seen.has(number.toLowerCase())) errors.push({ row, message: `Account number ${number} appears more than once in the file.` });
      else seen.add(number.toLowerCase());
    });
    const accepted = errors.length === 0 ? data.length : data.length - errors.length;
    if (!dryRun && errors.length === 0) {
      for (const line of data) {
        const [number = '', name = '', type = ''] = line.split(',').map((x) => x.trim());
        this.chart.push({
          id: crypto.randomUUID(), accountNumber: number, name, accountType: CHART_ACCOUNT_TYPES.find((t) => t.toLowerCase() === type.toLowerCase())!, isActive: true, source: 'CsvImport',
          externalReference: null, mappingUseCount: 0,
        });
      }
    }
    // All-or-nothing: with any rejected row a real import saves nothing.
    return { dryRun, accepted: !dryRun && errors.length > 0 ? 0 : accepted, rejected: errors.length, errors };
  }

  // ---------------------------------------------------------------- mappings

  listMappings(params?: ListMappingsParams): Paged<AccountingMapping> {
    if (!this.getCapabilities().canPreview) throw apiError(403, 'You do not have permission to view GL mappings.');
    const scenario = this.scenario();
    const rows = this.mappings
      .filter((m) => !(m.hiddenInScenario && m.hiddenInScenario === scenario) && (!params?.category || m.category === params.category) && (!params?.activeOnly || m.isActive))
      .sort((a, b) => (CATEGORY_ORDER.get(a.category) ?? 99) - (CATEGORY_ORDER.get(b.category) ?? 99) || (a.subcategory ?? '').localeCompare(b.subcategory ?? '') || b.mappingVersion - a.mappingVersion);
    const pageSize = params?.pageSize ?? 200;
    const page = params?.page ?? 1;
    const items = rows.slice((page - 1) * pageSize, page * pageSize).map((m) => {
      const { hiddenInScenario, ...rest } = m;
      void hiddenInScenario;
      return structuredClone(rest);
    });
    return { items, page, pageSize, totalCount: rows.length };
  }

  createMapping(input: CreateMappingInput): AccountingMapping {
    this.require('canManageMappings', 'manage GL mappings');
    const invalid = (detail: string, status = 400, code: string = ExportErrorCode.MappingInvalid) => apiError(status, detail, code);
    const side = categorySide(input.category);
    if (!side) throw invalid('Choose a payroll category.');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(input.effectiveFrom)) throw invalid('Enter the date this mapping takes effect.');
    let account: ChartAccount | undefined;
    if (input.treatment === 'NonPosting') {
      if (!input.nonPostingReason?.trim()) throw invalid('A non-posting mapping needs a reason.');
      if (input.nonPostingReason.trim().length > 300) throw invalid('The reason must be 300 characters or fewer.');
    } else {
      if (!input.chartAccountId) throw invalid('Choose the account this category posts to.');
      account = this.chart.find((a) => a.id === input.chartAccountId);
      if (!account) throw invalid('That account was not found in your chart of accounts.');
      if (!account.isActive) throw invalid(`Account ${account.accountNumber} is archived and cannot be used in a new mapping.`, 409, ExportErrorCode.AccountArchived);
      if (input.category === 'NetPayClearing' && account.accountType === 'Asset' && !input.confirmedFundingModel) {
        throw invalid('Net pay should post to a Clearing or Liability account. Mapping it to a bank (asset) account changes how payroll funding is represented: confirm to continue.');
      }
    }
    const groupDimension = input.groupDimension ?? null;
    const groupKey = input.groupKey?.trim() || null;
    // Both group qualifiers or neither, and only for gross wages (contract section 3).
    if ((groupDimension === null) !== (groupKey === null)) throw invalid('A group mapping needs both a group dimension and a group.');
    if (groupDimension !== null) {
      if (input.category !== 'EarningsGross') throw invalid('Group mappings apply to gross wages only.');
      if (!WAGE_GROUP_DIMENSIONS.includes(groupDimension)) throw invalid('Choose a group dimension from the list.');
      const sameScope = this.mappings.find((m) => m.isActive && m.category === input.category && m.groupDimension === groupDimension && m.groupKey === groupKey && m.subcategory === (input.subcategory?.trim() || null)
        && m.treatment === input.treatment && m.effectiveFrom <= input.effectiveFrom && (m.effectiveTo === null || input.effectiveFrom <= m.effectiveTo));
      if (sameScope) throw invalid('An active mapping already exists for this group. End it first, then add the new one.', 409);
    }
    this.mappingVersionCounter += 1;
    const mapping: StoredMapping = {
      id: crypto.randomUUID(), category: input.category, subcategory: input.subcategory?.trim() || null, payrollCode: input.payrollCode?.trim() || null,
      employeeId: input.employeeId?.trim() || null, departmentId: input.departmentId?.trim() || null, locationCode: input.locationCode?.trim() || null, groupDimension, groupKey, treatment: input.treatment,
      nonPostingReason: input.treatment === 'NonPosting' ? input.nonPostingReason!.trim() : null, chartAccountId: account?.id ?? null, accountNumber: account?.accountNumber ?? null,
      accountName: account?.name ?? null, accountType: account?.accountType ?? null, direction: side, effectiveFrom: input.effectiveFrom, effectiveTo: null,
      mappingVersion: this.mappingVersionCounter, isActive: true, confirmedFundingModel: input.confirmedFundingModel === true,
    };
    this.mappings.push(mapping);
    this.recomputeUseCounts();
    const { hiddenInScenario, ...rest } = mapping;
    void hiddenInScenario;
    return structuredClone(rest);
  }

  endMapping(id: string): void {
    this.require('canManageMappings', 'manage GL mappings');
    const mapping = this.mappings.find((m) => m.id === id);
    if (!mapping) throw apiError(404, 'That mapping was not found.');
    if (!mapping.isActive) throw apiError(409, 'That mapping has already ended.', ExportErrorCode.InvalidTransition);
    mapping.isActive = false;
    mapping.effectiveTo = mapping.effectiveFrom > todayIso() ? mapping.effectiveFrom : todayIso();
    this.mappingVersionCounter += 1;
    this.recomputeUseCounts();
  }

  getMappingReadiness(payrollRunId: string): MappingReadiness {
    if (!this.getCapabilities().canPreview) throw apiError(403, 'You do not have permission to view GL mappings.');
    const run = this.requireRun(payrollRunId);
    const preview = this.evaluate(run, run.currentVersion, 'Compound', run.checkDate, 'Original', null, null, this.legacyOptions('Compound'));
    return {
      ready: preview.unmapped.length === 0,
      missing: preview.unmapped.filter((u) => u.reason !== 'Ambiguous'),
      ambiguous: preview.unmapped.filter((u) => u.reason === 'Ambiguous'),
    };
  }

  // ---------------------------------------------------------------- export options and settings

  private legacyOptions(mode: ExportMode): ExportOptions {
    return {
      netPayDetailMode: mode === 'PerEmployee' ? 'PerEmployee' : 'Consolidated', wageMappingMode: 'LegacyResolver', wageGroupDimension: null, source: 'LegacyDefault', recorded: true,
      settingsVersion: null, groupConfigVersion: null,
    };
  }

  /** Request option, else company setting, else the original behavior (`LegacyDefault`). A journal in the per-employee mode keeps everything per employee, so its net pay detail is not selectable. */
  private effectiveOptions(input: ExportOptionsInput, mode: ExportMode): ExportOptions {
    const s = this.settings;
    if (input.netPayDetailMode !== undefined && !NET_PAY_DETAIL_MODES.includes(input.netPayDetailMode)) throw apiError(400, 'Choose one net pay line for each employee, or one consolidated line.', ExportErrorCode.OptionInvalid);
    if (input.wageMappingMode !== undefined && !SELECTABLE_WAGE_MODES.includes(input.wageMappingMode)) throw apiError(400, 'Choose Single account or By group.', ExportErrorCode.OptionInvalid);
    if (input.wageGroupDimension !== undefined && !WAGE_GROUP_DIMENSIONS.includes(input.wageGroupDimension)) throw apiError(400, 'Choose a group dimension from the list.', ExportErrorCode.OptionInvalid);
    const requested = input.netPayDetailMode !== undefined || input.wageMappingMode !== undefined || input.wageGroupDimension !== undefined;
    if (!requested && !s.isConfigured) return this.legacyOptions(mode);
    const wageMode = input.wageMappingMode ?? (s.isConfigured ? s.wageMappingMode : 'LegacyResolver');
    const companyDimension = s.isConfigured && s.wageMappingMode === 'GroupBased' ? s.wageGroupDimension : null;
    return {
      netPayDetailMode: mode === 'PerEmployee' ? 'PerEmployee' : (input.netPayDetailMode ?? (s.isConfigured ? s.netPayDetailMode : 'Consolidated')),
      wageMappingMode: wageMode, wageGroupDimension: wageMode === 'GroupBased' ? (input.wageGroupDimension ?? companyDimension) : null,
      source: requested ? 'Request' : 'CompanySettings', recorded: true, settingsVersion: s.settingsVersion, groupConfigVersion: this.groupConfigVersion,
    };
  }

  private requirePerEmployeeFor(mode: ExportMode, options: ExportOptions): void {
    if ((mode === 'PerEmployee' || options.netPayDetailMode === 'PerEmployee') && !this.getCapabilities().canViewPerEmployee) {
      throw apiError(403, 'Per-employee detail needs the employee compensation permission.', ExportErrorCode.PerEmployeeNotPermitted);
    }
  }

  /** The company-default mapping of a category (no qualifier at all): the single source of truth for the default wage and clearing accounts. */
  private defaultAccount(category: string): DefaultAccountRef | null {
    const found = this.activeMappings(todayIso())
      .filter((m) => m.category === category && m.treatment === 'Post' && m.subcategory === null && m.payrollCode === null && m.employeeId === null && m.departmentId === null && m.locationCode === null && m.groupDimension === null)
      .sort((a, b) => b.mappingVersion - a.mappingVersion)[0];
    return found ? { mappingId: found.id, accountNumber: found.accountNumber ?? '', accountName: found.accountName ?? '' } : null;
  }

  getExportSettings(): ExportSettings {
    this.require('canPreview', 'view export settings');
    return { ...structuredClone(this.settings), groupConfigVersion: this.groupConfigVersion, defaultWageAccount: this.defaultAccount('EarningsGross'), defaultClearingAccount: this.defaultAccount('NetPayClearing') };
  }

  saveExportSettings(input: SaveExportSettingsInput): ExportSettings {
    this.require('canManageMappings', 'change export settings');
    const invalid = (detail: string, code: string = ExportErrorCode.OptionInvalid, status = 400) => apiError(status, detail, code);
    if (!NET_PAY_DETAIL_MODES.includes(input.netPayDetailMode)) throw invalid('Choose a net pay detail mode.');
    if (!SELECTABLE_WAGE_MODES.includes(input.wageMappingMode as never)) throw invalid('Choose Single account or By group. The legacy rules cannot be saved as a setting.');
    if (input.wageMappingMode === 'GroupBased' && !input.wageGroupDimension) throw invalid('By group needs a group dimension.');
    if (input.expectedVersion !== this.settings.settingsVersion) throw invalid('These settings were changed by someone else since you opened them. Reload to see the current values.', ExportErrorCode.VersionConflict, 409);
    if (input.wageMappingMode === 'SingleAccount' && !this.defaultAccount('EarningsGross')) {
      throw invalid('Single account needs an active company-default Gross wages mapping. Add one in GL Mapping first.', ExportErrorCode.DefaultWageAccountRequired, 409);
    }
    this.settings = {
      ...this.settings, netPayDetailMode: input.netPayDetailMode, wageMappingMode: input.wageMappingMode, wageGroupDimension: input.wageMappingMode === 'GroupBased' ? input.wageGroupDimension ?? null : null,
      settingsVersion: this.settings.settingsVersion + 1, isConfigured: true, updatedAtUtc: this.now(), updatedByUserId: 'mock-user',
    };
    return this.getExportSettings();
  }

  // ---------------------------------------------------------------- group membership and wage resolution

  /** The employee's group for a dimension on a date. These are the employee's CURRENT attribute values (a documented limitation), not effective-dated to the check date. */
  private dimensionValue(dimension: WageGroupDimension, employeeId: string, date: string): DimensionValue {
    const emp = MOCK_EMPLOYEES[employeeId];
    const attrs = MOCK_EMPLOYEE_ATTRIBUTES[employeeId];
    const some = (key: string | null | undefined, label: string | null | undefined): DimensionValue => (key ? { kind: 'value', key, label: label || key } : { kind: 'none' });
    switch (dimension) {
      case 'Department': return some(emp?.departmentId, emp?.department);
      case 'WorkLocation': return some(attrs?.workLocationId, attrs?.workLocationLabel);
      case 'Role': return some(attrs?.roleCode, attrs?.roleLabel);
      case 'Discipline': return some(attrs?.disciplineId, attrs?.disciplineLabel);
      case 'EmploymentType': return some(attrs?.employmentType, attrs?.employmentType ? EMPLOYMENT_TYPE_LABEL[attrs.employmentType] : null);
      case 'EmployeeGroup': {
        const active = this.memberships.filter((m) => m.employeeId === employeeId && m.effectiveFrom <= date && (m.effectiveTo === null || date <= m.effectiveTo))
          .map((m) => this.groups.find((g) => g.id === m.groupId))
          .filter((g): g is EmployeeGroup => !!g && g.isActive && (g.effectiveFrom === null || g.effectiveFrom <= date) && (g.effectiveTo === null || date <= g.effectiveTo));
        const distinct = [...new Map(active.map((g) => [g.id, g])).values()];
        if (distinct.length > 1) return { kind: 'ambiguous' };
        return distinct[0] ? { kind: 'value', key: distinct[0].id, label: distinct[0].name } : { kind: 'none' };
      }
    }
  }

  private resolveLine(line: InternalSource, date: string, options: ExportOptions): Resolution {
    if (line.category !== 'EarningsGross' || options.source === 'LegacyDefault' || !options.wageMappingMode || options.wageMappingMode === 'LegacyResolver') return this.resolve(line, date);
    return this.resolveWage(line, date, options);
  }

  private wageCandidates(date: string): StoredMapping[] {
    const accountActive = (m: StoredMapping) => m.treatment === 'NonPosting' || !!this.chart.find((a) => a.id === m.chartAccountId)?.isActive;
    return this.activeMappings(date).filter((m) => m.category === 'EarningsGross' && m.subcategory === null && accountActive(m));
  }

  /** The group mappings for one group: a mapping carrying that group, or (for the Department dimension) an existing department-scoped mapping, which keeps working. */
  private groupMappingsFor(candidates: StoredMapping[], dimension: WageGroupDimension, key: string): StoredMapping[] {
    return candidates.filter((m) => m.employeeId === null && m.payrollCode === null && m.locationCode === null && (
      (m.groupDimension === dimension && m.groupKey === key && m.departmentId === null) || (dimension === 'Department' && m.groupDimension === null && m.departmentId === key)));
  }

  /** Contract section 3. SingleAccount: earning-code mapping, then the company default (employee, group, department and location wage mappings are never used).
   * GroupBased: employee override, then the group mapping for the dimension; a group with no mapping and no override BLOCKS (the default is never substituted silently). */
  private resolveWage(line: InternalSource, date: string, options: ExportOptions): Resolution {
    const candidates = this.wageCandidates(date);
    const mapped = (m: StoredMapping, level: string, text: string, group: MappedResolution['group'] = null): Resolution => ({ kind: 'mapped', mapping: m, level, reason: `${text} (version ${m.mappingVersion})`, group });
    const one = (pool: StoredMapping[], level: string, text: string, group?: MappedResolution['group']): Resolution | null => {
      if (pool.length === 0) return null;
      return pool.length > 1 ? { kind: 'unmapped', reason: 'Ambiguous' } : mapped(pool[0]!, level, text, group);
    };
    const unscoped = (m: StoredMapping) => m.employeeId === null && m.departmentId === null && m.locationCode === null && m.groupDimension === null;
    const byCode = candidates.filter((m) => unscoped(m) && m.payrollCode !== null && m.payrollCode === line.payrollCode);
    const byDefault = candidates.filter((m) => unscoped(m) && m.payrollCode === null);

    if (options.wageMappingMode === 'SingleAccount') {
      const found = one(byCode, 'PayrollCode', 'Earning-code mapping') ?? one(byDefault, 'CompanyDefault', 'Company default wage mapping');
      return found ?? { kind: 'blocked', code: 'DefaultWageAccountRequired', label: '' };
    }
    const dimension = options.wageGroupDimension;
    if (!dimension) return { kind: 'blocked', code: 'WageGroupDimensionRequired', label: '' };
    const override = one(candidates.filter((m) => m.employeeId === line.employeeId), 'EmployeeOverride', 'Employee override');
    if (override) return override;
    const value = this.dimensionValue(dimension, line.employeeId, date);
    if (value.kind === 'none') return { kind: 'blocked', code: 'GroupMembershipMissing', label: DIMENSION_NAMES[dimension] };
    if (value.kind === 'ambiguous') return { kind: 'blocked', code: 'EmployeeGroupAmbiguous', label: DIMENSION_NAMES[dimension] };
    const group = { dimension, key: value.key, label: value.label };
    const found = one(this.groupMappingsFor(candidates, dimension, value.key), 'Group', `Group mapping for ${DIMENSION_NAMES[dimension]} '${value.label}'`, group);
    return found ?? { kind: 'blocked', code: 'GroupMappingMissing', label: `${DIMENSION_NAMES[dimension]} '${value.label}'` };
  }

  // ---------------------------------------------------------------- the "By group" grid

  getWageGroups(params: WageGroupParams): WageGroupGrid {
    this.require('canPreview', 'view GL mappings');
    if (!WAGE_GROUP_DIMENSIONS.includes(params.dimension)) throw apiError(400, 'Choose a group dimension from the list.', ExportErrorCode.OptionInvalid);
    const run = this.requireRun(params.payrollRunId);
    const date = params.accountingDate || run.checkDate;
    const calcs = this.calculationsFor(run, run.currentVersion);
    const candidates = this.wageCandidates(date);
    const rows = new Map<string, { label: string; employees: Set<string>; mapping: StoredMapping | null; status: WageGroupStatus }>();
    let employeesWithoutGroup = 0;
    for (const calc of calcs) {
      const value = this.dimensionValue(params.dimension, calc.employeeId, date);
      if (value.kind !== 'value') {
        employeesWithoutGroup += 1;
        continue;
      }
      let row = rows.get(value.key);
      if (!row) {
        // The grid shows the GROUP's own wage mapping (an employee override does not make the group mapped).
        const pool = this.groupMappingsFor(candidates, params.dimension, value.key);
        row = { label: value.label, employees: new Set(), mapping: pool.length === 1 ? pool[0]! : null, status: pool.length === 0 ? 'Missing' : pool.length === 1 ? 'Mapped' : 'Ambiguous' };
        rows.set(value.key, row);
      }
      row.employees.add(calc.employeeId);
    }
    const groups: WageGroupRow[] = [...rows.entries()].map(([groupKey, r]) => ({
      groupKey, label: r.label, employeeCount: r.employees.size, status: r.status, account: r.mapping ? { accountNumber: r.mapping.accountNumber ?? '', accountName: r.mapping.accountName ?? '' } : null,
      mappingId: r.mapping?.id ?? null, mappingVersion: r.mapping?.mappingVersion ?? null,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return { dimension: params.dimension, accountingDate: date, groups, employeesWithoutGroup };
  }

  // ---------------------------------------------------------------- employee groups

  private groupWithCount(g: EmployeeGroup): EmployeeGroup {
    const today = todayIso();
    return { ...structuredClone(g), memberCount: this.memberships.filter((m) => m.groupId === g.id && (m.effectiveTo === null || m.effectiveTo >= today)).length };
  }

  private findGroup(id: string): EmployeeGroup {
    const group = this.groups.find((g) => g.id === id);
    if (!group) throw apiError(404, 'That employee group was not found.', ExportErrorCode.GroupNotFound);
    return group;
  }

  listEmployeeGroups(): EmployeeGroup[] {
    this.require('canPreview', 'view employee groups');
    return this.groups.map((g) => this.groupWithCount(g)).sort((a, b) => a.name.localeCompare(b.name));
  }

  private groupNameProblem(name: string, excludeId?: string): string | null {
    const trimmed = name.trim();
    if (!trimmed) return 'Enter a group name.';
    if (trimmed.length > 100) return 'The group name must be 100 characters or fewer.';
    if (this.groups.some((g) => g.id !== excludeId && g.name.trim().toLowerCase().replace(/\s+/g, ' ') === trimmed.toLowerCase().replace(/\s+/g, ' '))) return `A group named ${trimmed} already exists.`;
    return null;
  }

  createEmployeeGroup(input: CreateEmployeeGroupInput): EmployeeGroup {
    this.require('canManageMappings', 'manage employee groups');
    const problem = this.groupNameProblem(input.name);
    if (problem) throw apiError(problem.includes('already exists') ? 409 : 400, problem, problem.includes('already exists') ? ExportErrorCode.DuplicateName : ExportErrorCode.OptionInvalid);
    const group: EmployeeGroup = {
      id: crypto.randomUUID(), name: input.name.trim(), description: input.description?.trim() || null, isActive: true, effectiveFrom: input.effectiveFrom || todayIso(), effectiveTo: null,
      memberCount: 0, concurrencyVersion: 1,
    };
    this.groups.push(group);
    this.groupConfigVersion += 1;
    return this.groupWithCount(group);
  }

  updateEmployeeGroup(id: string, input: UpdateEmployeeGroupInput): EmployeeGroup {
    this.require('canManageMappings', 'manage employee groups');
    const group = this.findGroup(id);
    if (input.expectedVersion !== group.concurrencyVersion) throw apiError(409, 'This group was changed by someone else since you opened it. Reload to see the current values.', ExportErrorCode.VersionConflict);
    const problem = this.groupNameProblem(input.name, id);
    if (problem) throw apiError(problem.includes('already exists') ? 409 : 400, problem, problem.includes('already exists') ? ExportErrorCode.DuplicateName : ExportErrorCode.OptionInvalid);
    if (input.effectiveTo && group.effectiveFrom && input.effectiveTo < group.effectiveFrom) throw apiError(400, 'The end date cannot be before the start date.', ExportErrorCode.OptionInvalid);
    group.name = input.name.trim();
    group.description = input.description?.trim() || null;
    group.effectiveTo = input.effectiveTo || null;
    group.concurrencyVersion += 1;
    this.groupConfigVersion += 1;
    return this.groupWithCount(group);
  }

  setEmployeeGroupActive(id: string, active: boolean): EmployeeGroup {
    this.require('canManageMappings', 'manage employee groups');
    const group = this.findGroup(id);
    if (group.isActive !== active) {
      group.isActive = active;
      group.concurrencyVersion += 1;
      this.groupConfigVersion += 1;
    }
    return this.groupWithCount(group);
  }

  listEmployeeGroupMembers(groupId: string): EmployeeGroupMember[] {
    this.require('canPreview', 'view employee groups');
    // Members are employee numbers, so they need the employee read permission as well.
    if (!this.getCapabilities().canViewPerEmployee) throw apiError(403, 'Viewing group members needs permission to view employees.', ExportErrorCode.PerEmployeeNotPermitted);
    this.findGroup(groupId);
    return this.memberships.filter((m) => m.groupId === groupId)
      .map((m) => ({ membershipId: m.membershipId, employeeReference: MOCK_EMPLOYEES[m.employeeId]?.reference ?? 'Unknown', effectiveFrom: m.effectiveFrom, effectiveTo: m.effectiveTo }))
      .sort((a, b) => a.employeeReference.localeCompare(b.employeeReference) || a.effectiveFrom.localeCompare(b.effectiveFrom));
  }

  addEmployeeGroupMembers(groupId: string, input: AddGroupMembersInput): void {
    this.require('canManageMappings', 'manage employee groups');
    this.findGroup(groupId);
    if (input.employeeIds.length < 1 || input.employeeIds.length > 500) throw apiError(400, 'Choose between 1 and 500 employees.', ExportErrorCode.OptionInvalid);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(input.effectiveFrom)) throw apiError(400, 'Enter the date the membership starts.', ExportErrorCode.OptionInvalid);
    if (input.effectiveTo && input.effectiveTo < input.effectiveFrom) throw apiError(400, 'The end date cannot be before the start date.', ExportErrorCode.OptionInvalid);
    const ids = [...new Set(input.employeeIds)];
    // Every employee must belong to this company (the server verifies it through the Employees service); anything else is a plain 404.
    if (ids.some((id) => !MOCK_EMPLOYEES[id])) throw apiError(404, 'One or more employees were not found for this company.', ExportErrorCode.EmployeeNotFound);
    const to = input.effectiveTo ?? null;
    const overlaps = (m: StoredMembership) => m.groupId === groupId && m.effectiveFrom <= (to ?? '9999-12-31') && (m.effectiveTo === null || input.effectiveFrom <= m.effectiveTo);
    if (ids.some((id) => this.memberships.some((m) => m.employeeId === id && overlaps(m)))) throw apiError(409, 'An employee already has an overlapping membership in this group. End it first.', ExportErrorCode.OverlappingMembership);
    for (const employeeId of ids) this.memberships.push({ membershipId: crypto.randomUUID(), groupId, employeeId, effectiveFrom: input.effectiveFrom, effectiveTo: to });
    this.groupConfigVersion += 1;
  }

  endEmployeeGroupMembership(membershipId: string, effectiveTo: string): void {
    this.require('canManageMappings', 'manage employee groups');
    const membership = this.memberships.find((m) => m.membershipId === membershipId);
    if (!membership) throw apiError(404, 'That membership was not found.', ExportErrorCode.GroupNotFound);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveTo) || effectiveTo < membership.effectiveFrom) throw apiError(400, 'The end date cannot be before the start date.', ExportErrorCode.OptionInvalid);
    membership.effectiveTo = effectiveTo;
    this.groupConfigVersion += 1;
  }

  listEmployeeCandidates(search?: string): EmployeeCandidate[] {
    this.require('canPreview', 'view employees');
    const term = (search ?? '').trim().toLowerCase();
    return Object.values(MOCK_EMPLOYEES)
      .filter((e) => !term || e.reference.toLowerCase().includes(term) || e.displayName.toLowerCase().includes(term))
      .map((e) => ({ id: e.id, employeeNumber: e.reference, name: e.displayName }));
  }

  // ---------------------------------------------------------------- code mappings (warning-only: they never change the journal)

  private codeItems(payrollRunId?: string): CodeReadinessItem[] {
    if (this.scenario() === 'codes-unavailable') return [];
    return MOCK_CODES.map((c) => {
      const payrollCode = payrollCodeKey(c.kind, c.codeId);
      const found = this.mappings.filter((m) => m.isActive && m.payrollCode === payrollCode && m.category === c.accountingCategory).sort((a, b) => b.mappingVersion - a.mappingVersion);
      const mapping = found[0];
      return {
        kind: c.kind, codeId: c.codeId, code: c.code, name: c.name, categoryCode: c.categoryCode, classification: c.classification, taxTreatment: c.taxTreatment, source: c.source,
        accountingCategory: c.accountingCategory, payrollCode, mappingStatus: found.length > 1 ? 'Ambiguous' : mapping ? 'Mapped' : 'Unmapped',
        mapping: mapping ? { id: mapping.id, accountNumber: mapping.accountNumber ?? '', accountName: mapping.accountName ?? '', effectiveFrom: mapping.effectiveFrom, effectiveTo: mapping.effectiveTo, version: mapping.mappingVersion } : null,
        isActive: true, employeesWithElection: payrollRunId ? c.elections : null, totalAmountInPayroll: null,
        unavailableReason: 'The payroll does not supply per-code amounts yet.',
      };
    });
  }

  getCodeReadiness(params?: CodeReadinessParams): CodeReadiness {
    this.require('canPreview', 'view code mappings');
    if (params?.payrollRunId) this.requireRun(params.payrollRunId);
    const unavailable = this.scenario() === 'codes-unavailable';
    const term = (params?.search ?? '').trim().toLowerCase();
    const rows = this.codeItems(params?.payrollRunId)
      .filter((c) => (!params?.kind || c.kind === params.kind) && (!term || (c.code ?? '').toLowerCase().includes(term) || c.name.toLowerCase().includes(term)))
      .sort((a, b) => CODE_KINDS_ORDER.indexOf(a.kind) - CODE_KINDS_ORDER.indexOf(b.kind) || a.name.localeCompare(b.name));
    const pageSize = params?.pageSize ?? 50;
    const page = params?.page ?? 1;
    const state = unavailable ? 'Unavailable' : 'Available';
    return {
      items: structuredClone(rows.slice((page - 1) * pageSize, page * pageSize)), page, pageSize, totalCount: rows.length,
      catalogs: { deductions: state, earnings: state, benefits: state }, appliesToJournal: false, notice: CODE_MAPPING_BANNER,
    };
  }

  /** What applying a bulk mapping would do. Never writes. An existing specific mapping is never overwritten unless `replaceExisting` is true. */
  previewCodeBulk(input: CodeBulkRequest): CodeBulkPreview {
    this.require('canManageMappings', 'manage code mappings');
    const errors: string[] = [];
    const account = this.chart.find((a) => a.id === input.chartAccountId);
    if (!account) errors.push('Choose an account from your chart of accounts.');
    else if (!account.isActive) errors.push(`Account ${account.accountNumber} is archived and cannot be used in a new mapping.`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(input.effectiveFrom)) errors.push('Enter the date the mappings take effect.');
    if (input.codes.length === 0) errors.push('Choose at least one code.');
    const catalog = new Map(this.codeItems().map((c) => [`${c.kind}|${c.codeId}`, c]));
    const seen = new Set<string>();
    const items: CodeBulkPreviewItem[] = [];
    for (const requested of input.codes) {
      const id = `${requested.kind}|${requested.codeId}`;
      if (seen.has(id)) continue;
      seen.add(id);
      const code = catalog.get(id);
      const base = { kind: requested.kind, codeId: requested.codeId, code: code?.code ?? null, name: code?.name ?? null, existingMappingId: null, existingAccount: null, reason: null };
      if (!code) {
        items.push({ ...base, action: 'Skip', reason: 'This code is not active or was not found.' });
        continue;
      }
      const existing = this.mappings.filter((m) => m.isActive && m.payrollCode === code.payrollCode && m.category === code.accountingCategory);
      const existingAccount = existing[0] ? { accountNumber: existing[0].accountNumber ?? '', accountName: existing[0].accountName ?? '' } : null;
      if (existing.length === 0) items.push({ ...base, action: 'Create' });
      else if (existing.length === 1 && existing[0]!.chartAccountId === input.chartAccountId) items.push({ ...base, action: 'Skip', existingMappingId: existing[0]!.id, existingAccount, reason: 'Already mapped to this account.' });
      else if (existing.length === 1 && input.replaceExisting) items.push({ ...base, action: 'Replace', existingMappingId: existing[0]!.id, existingAccount });
      else items.push({ ...base, action: 'Conflict', existingMappingId: existing[0]!.id, existingAccount, reason: existing.length > 1 ? 'More than one active mapping exists for this code. End the extra ones first.' : 'A different mapping exists. Tick Replace existing mappings to replace it.' });
    }
    const tally = (a: CodeBulkAction) => items.filter((i) => i.action === a).length;
    return { items, create: tally('Create'), replace: tally('Replace'), skip: tally('Skip'), conflict: tally('Conflict'), errors };
  }

  /** Atomic and idempotent: the same key and body answers the same result without writing again; the same key with another body is refused. Refused without `confirm`. */
  applyCodeBulk(input: CodeBulkApplyInput, confirm = true): CodeBulkResult {
    this.require('canManageMappings', 'manage code mappings');
    if (!confirm) throw apiError(400, 'Confirm the bulk mapping before it is applied.', ExportErrorCode.ConfirmRequired);
    if (!/^[A-Za-z0-9._:-]{1,100}$/.test(input.idempotencyKey)) throw apiError(400, 'The idempotency key must be 1 to 100 characters: letters, numbers and . _ : -', ExportErrorCode.OptionInvalid);
    const fingerprint = JSON.stringify({ codes: input.codes.map((c) => `${c.kind}|${c.codeId}`).sort(), a: input.chartAccountId, d: input.effectiveFrom, r: input.replaceExisting });
    const prior = this.bulkLedger.get(input.idempotencyKey);
    if (prior) {
      if (prior.fingerprint !== fingerprint) throw apiError(409, 'That idempotency key was already used for a different request.', ExportErrorCode.IdempotencyKeyReused);
      return { ...prior.result, replayed: true };
    }
    const preview = this.previewCodeBulk(input);
    if (preview.errors.length > 0) throw apiError(400, preview.errors[0]!, ExportErrorCode.MappingInvalid);
    const account = this.chart.find((a) => a.id === input.chartAccountId)!;
    for (const item of preview.items) {
      if (item.action !== 'Create' && item.action !== 'Replace') continue;
      const code = MOCK_CODES.find((c) => c.kind === item.kind && c.codeId === item.codeId)!;
      if (item.action === 'Replace') {
        const old = this.mappings.find((m) => m.id === item.existingMappingId);
        if (old) {
          old.isActive = false;
          old.effectiveTo = old.effectiveFrom > input.effectiveFrom ? old.effectiveFrom : input.effectiveFrom;
        }
      }
      this.mappingVersionCounter += 1;
      this.mappings.push({
        id: crypto.randomUUID(), category: code.accountingCategory, subcategory: null, payrollCode: payrollCodeKey(code.kind, code.codeId), employeeId: null, departmentId: null, locationCode: null,
        groupDimension: null, groupKey: null, treatment: 'Post', nonPostingReason: null, chartAccountId: account.id, accountNumber: account.accountNumber, accountName: account.name,
        accountType: account.accountType, direction: categorySide(code.accountingCategory) ?? 'Debit', effectiveFrom: input.effectiveFrom, effectiveTo: null, mappingVersion: this.mappingVersionCounter,
        isActive: true, confirmedFundingModel: false,
      });
    }
    this.recomputeUseCounts();
    const result: CodeBulkResult = { created: preview.create, replaced: preview.replace, skipped: preview.skip, conflicts: preview.conflict, replayed: false };
    this.bulkLedger.set(input.idempotencyKey, { fingerprint, result });
    return result;
  }
}
